import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { format, parseISO } from "date-fns";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { environment } from "@environments/environment";

import { ShareMessageResponse } from "@shared/Interfaces/share-message-response.interface";
import { ParamsService } from "@shared/Services/params.service";

import { Dropdown } from "@salud/shared/Types/dropdown.enum";

const { enlineaBackendUrl } = environment;

import { CreditNotesFilter, CreditNotesReportFilter } from "../Types/Interfaces/credit-notes-filter.interface";
import { CreditNotes } from "../Types/Interfaces/credit-notes.interface";
import { CreditNoteEmail } from "../Types/Interfaces/CreditNoteEmail.interface";
import { DropdownSeguros } from "../Types/Interfaces/dropdown-seguros.interface";

@Injectable({
  providedIn: "root",
})
export class CreditNotesRepository {
  constructor(private http: HttpClient, private paramsService: ParamsService) {}

  public getEstatus(): Observable<DropdownSeguros[]> {
    return this.http.get<DropdownSeguros[]>(`${enlineaBackendUrl}/api/insurances/Invoices/Status`);
  }

  public getDocumentTypes(): Observable<DropdownSeguros[]> {
    return this.http.get<DropdownSeguros[]>(`${enlineaBackendUrl}/api/insurances/Invoices/GetInvoiceDocumentTypes`);
  }

  public getTiposIdentificcion(): Observable<DropdownSeguros[]> {
    return this.http.get<DropdownSeguros[]>(
      `${enlineaBackendUrl}/api/insurances/Common/Dropdown/${Dropdown.TipoIdentificacionSeguro}`
    );
  }

  public getCreditNotes(polizaFilter: CreditNotesFilter): Observable<CreditNotes[]> {
    const params = this.paramsService.mapRequestParams(polizaFilter);
    return this.http.get<CreditNotes[]>(`${enlineaBackendUrl}/api/insurances/Invoices`, { params }).pipe(
      map((creditNotes) => {
        const dateFormat = "dd/MM/yyyy";
        return creditNotes.map((creditNote) => ({
          ...creditNote,
          documentTypeDescription: creditNote.documentType === "F" ? "Factura" : "Nota de Crédito",
          contractorInformation: {
            ...creditNote.contractorInformation,
            fullName: `${creditNote.contractorInformation?.firstName} ${creditNote.contractorInformation?.lastName}`,
          },
          invoiceInformation: {
            ...creditNote.invoiceInformation,
            validity:
              (creditNote?.invoiceInformation?.validityStart
                ? `${format(parseISO(creditNote?.invoiceInformation?.validityStart), dateFormat)}`
                : "") +
              ` - ` +
              (creditNote?.invoiceInformation?.validityEnd
                ? `${format(parseISO(creditNote?.invoiceInformation?.validityEnd), dateFormat)}`
                : ""),
            validityStart: creditNote?.invoiceInformation?.validityStart
              ? format(parseISO(creditNote?.invoiceInformation?.validityStart), dateFormat)
              : "",
          },
          policyInformation: {
            ...creditNote.policyInformation,
            formattedPolicy: `${creditNote.policyInformation.productCode}-${creditNote.policyInformation.policyNumber}`,
          },
        }));
      })
    );
  }

  public getHealthPolicies(): Observable<DropdownSeguros[]> {
    return this.http.get<DropdownSeguros[]>(`${enlineaBackendUrl}/api/insurances/Invoices/InspireProducts`);
  }

  public getCreditNotesReport(creditNotesReportFilter: CreditNotesReportFilter): Observable<Blob> {
    const params = this.paramsService.mapRequestParams(creditNotesReportFilter);
    return this.http.get<Blob>(`${enlineaBackendUrl}/api/insurances/Invoices/InvoiceReport`, {
      params,
      responseType: "blob" as "json",
    });
  }

  public sendEmailCreditNote(request: CreditNoteEmail): Observable<ShareMessageResponse> {
    return this.http.post<ShareMessageResponse>(`${enlineaBackendUrl}/api/insurance/CreditNotes/email`, request);
  }
}
